import { RefObject } from 'react';
import { MotionValue, m } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
//
import { TYPE } from '../../../theme/stage';
import { WORDMARK } from '../copy';
import { CSS_EASE } from '../motion';
import BisqueRing from '../primitives/BisqueRing';
import { entrance } from '../primitives/entrance';

// ----------------------------------------------------------------------

export type WordmarkMotion = {
  leftX: MotionValue<number>;
  rightX: MotionValue<number>;
  face: MotionValue<string>;
  oScale: MotionValue<number>;
  oTurn: MotionValue<number>;
};

type Props = {
  mv: WordmarkMotion;
  animated: boolean;
  pinned: boolean;
  markRef: RefObject<HTMLDivElement>;
  ringPosRef: RefObject<HTMLSpanElement>;
};

type HalfProps = {
  side: 'left' | 'right';
  x: MotionValue<number>;
  face: MotionValue<string>;
  pinned: boolean;
};

const pressIn = keyframes`
  from { opacity: 0; letter-spacing: -0.02em; }
`;

const HALF = {
  left: { text: WORDMARK.left, kern: '-0.018em' },
  right: { text: WORDMARK.right, kern: undefined },
};

function Half({ side, x, face, pinned }: HalfProps) {
  const { text, kern } = HALF[side];

  return (
    <m.span
      className={`half ${side}`}
      style={{
        display: 'inline-block',
        marginRight: kern,
        willChange: pinned ? 'transform' : undefined,
        color: face,
        x,
      }}
    >
      <Box
        component="span"
        className="base"
        data-enter
        sx={entrance(pressIn, { duration: 1100, delay: 150, ease: CSS_EASE.press })}
      >
        {text}
      </Box>
    </m.span>
  );
}

export default function WordmarkDisplay({ mv, animated, pinned, markRef, ringPosRef }: Props) {
  return (
    <Box
      ref={markRef}
      className="mark"
      aria-hidden
      translate="no"
      sx={{
        ...TYPE.wordmarkDisplay,
        position: 'relative',
        display: 'flex',
        alignItems: 'baseline',
        whiteSpace: 'nowrap',
        mb: '.36em',
      }}
    >
      <Half side="left" x={mv.leftX} face={mv.face} pinned={pinned} />

      <Box component="span" sx={{ position: 'relative', display: 'inline-grid', mr: '-0.01em' }}>
        <Box
          component="span"
          className="glyph"
          sx={{ color: 'transparent', WebkitTextFillColor: 'transparent' }}
        >
          {WORDMARK.o}
        </Box>

        <Box
          ref={ringPosRef}
          component="span"
          sx={{
            position: 'absolute',
            left: '-0.0158em',
            top: '.1622em',
            width: '.6496em',
            height: '.6496em',
          }}
        >
          <m.span
            className="oScale"
            style={{
              position: 'absolute',
              inset: 0,
              willChange: animated ? 'transform' : undefined,
              scale: mv.oScale,
              rotate: mv.oTurn,
            }}
          >
            <BisqueRing size="hero" enter="load" />
          </m.span>
        </Box>
      </Box>

      <Half side="right" x={mv.rightX} face={mv.face} pinned={pinned} />
    </Box>
  );
}
